import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Flex } from '../atom/Layout';
import { Text } from '../atom/Text';
import { Button } from '../atom/Button';
import { CheckIcon } from '../atom/Icon';
import { OnlyInput } from '../atom/Input';
import { HorizonGreyLine } from '../atom/HorizonGreyLine';

import { useHistory } from 'react-router-dom';

import useVoucher from '../../../Hooks/useVoucher';
import Loading from './Loading';
import { AFFILIATE_LIST } from '../../../domain/affiliate/AffiliateListData';
import { STATUS_MESSAGE } from '../../../lib/Error';
import { getBabyfaceItemId } from '../../../domain/Sales/Item/BabyfaceAPI';

const VoucherCoupon = ({ status, couponOnly, onSelectVoucherCoupon }) => {
  const {
    notiVoucher,
    labelAccount,
    buttonRefresh,
    placeHolderCode,
    buttonEnroll,
    notiEmptyVoucher,
    notiDelegated,
    notiUse,
    notiUsed,
    notiExpired,
    notiUnusable,
  } = VOUCHER;

  const {
    isRegisterLoading,
    isLoading,
    isVoucherCouponLoaded,
    voucherCouponError,
    voucherCouponList,
    selectedVoucherCouponList,
    affiliateCouponId,
    fetchVoucherCouponList,
    registerVoucherCoupon,
    registerVoucherCouponBackground,
    getVoucherCouponSelectionList,
    setVoucherCouponSelectionList,
    setIsLoading,
    calculateDday,
    replaceDateFormat,
  } = useVoucher({ status });

  const [ codeInputValue, setCodeInputValue ] = useState('');

  const history = useHistory();

  const isCheckBoxOn = status === 'usable';

  // 세션에서 쿠폰 코드 배열 가져오기 (기존 단일 문자열 호환 지원)
  const getPreSelectedVoucherCodes = () => {
    const preSelectedVoucherCodes = sessionStorage.preSelectedVoucherCodes;
    if (preSelectedVoucherCodes) {
      try {
        return JSON.parse(preSelectedVoucherCodes);
      } catch (e) {
        return [];
      }
    }
    // 기존 단일 문자열 형식 호환
    const preSelectedVoucherCode = sessionStorage.preSelectedVoucherCode;
    return preSelectedVoucherCode ? [preSelectedVoucherCode] : [];
  };

  const preSelectedVoucherCouponCodes = useMemo(() => {
    if (isCheckBoxOn) {
      return getPreSelectedVoucherCodes();
    }
    return [];
  }, []);

  // 모든 preSelected 쿠폰 코드를 백엔드에 등록
  useEffect(() => {
    if (!isCheckBoxOn || !isVoucherCouponLoaded || preSelectedVoucherCouponCodes.length === 0) return;
    
    // 모든 쿠폰 코드를 백엔드에 등록 (하나가 실패해도 다음 쿠폰 처리 계속)
    const registerAllCoupons = async () => {
      const codesToRegister = preSelectedVoucherCouponCodes.filter(code => {
        const codeVoucherCoupon = voucherCouponList.find(el => el.code === code);
        return !codeVoucherCoupon; // 목록에 없는 쿠폰만 등록
      });
      
      // 쿠폰 등록 전에 쿠폰 목록 콘솔 출력
      console.log('쿠폰 등록 전 쿠폰 목록:', {
        preSelectedVoucherCouponCodes,
        codesToRegister,
        voucherCouponList: voucherCouponList.map(coupon => ({ id: coupon.id, code: coupon.code }))
      });
      
      // 모든 쿠폰을 병렬로 등록 시도 (하나가 실패해도 나머지 계속 처리)
      await Promise.allSettled(
        codesToRegister.map(code => registerVoucherCouponBackground(code))
      );
    };
    
    registerAllCoupons();
  }, [preSelectedVoucherCouponCodes, voucherCouponList, isVoucherCouponLoaded, isCheckBoxOn, registerVoucherCouponBackground]);

  const preSelectedVoucherCouponIds = useMemo(() => {
    if (!isCheckBoxOn || !isVoucherCouponLoaded) return [];
    
    if (sessionStorage.preSelectedVoucherId) {
      const voucherId = JSON.parse(sessionStorage.preSelectedVoucherId);
      return Array.isArray(voucherId) ? voucherId : [voucherId];
    } else if (preSelectedVoucherCouponCodes.length > 0) {
      // 모든 쿠폰 코드에 대해 ID 찾기
      return preSelectedVoucherCouponCodes
        .map(code => {
          const codeVoucherCoupon = voucherCouponList.find(el => el.code === code);
          return codeVoucherCoupon ? codeVoucherCoupon.id : null;
        })
        .filter(id => id !== null);
    }
    return [];
  }, [preSelectedVoucherCouponCodes, voucherCouponList, isVoucherCouponLoaded, isCheckBoxOn]);

  useEffect(() => {
    if (!isCheckBoxOn) return;
    // 모든 preSelected 쿠폰 ID와 제휴사 쿠폰 ID를 선택
    const voucherCouponSelection = [
      ...preSelectedVoucherCouponIds,
      affiliateCouponId
    ].filter(n => n);
    onSelectVoucherCoupon(voucherCouponSelection, setVoucherCouponSelectionList);
  }, [preSelectedVoucherCouponIds, affiliateCouponId, isCheckBoxOn]);

  // 제휴사 쿠폰이 보이지 않도록 필터링
  const affiCouponList = AFFILIATE_LIST.map(a => a.code);
  const filteredVoucherCouponList = useMemo(() => voucherCouponList.filter(el => !affiCouponList.includes(el.code)), [voucherCouponList]);

  return isLoading ? (
    <Loading onlyLoader={isCheckBoxOn} />
  ) : (
    <>
      <Flex justifyContent="space-between">
        <Text
          size={isCheckBoxOn ? 12 : 16}
          color={isCheckBoxOn ? 'brownGrey' : 'darkest'}
          weight={isCheckBoxOn ? 'ragular' : 'medium'}
        >
          {notiVoucher}{' '}
          <Text
            color={isCheckBoxOn ? 'darkest' : 'orangePink'}
            size={isCheckBoxOn ? 12 : 16}
            weight="bold"
          >
            {filteredVoucherCouponList.length}
            {labelAccount}
          </Text>
        </Text>
        <Text
          color="brownGrey"
          size={12}
          style={{ textDecoration: 'underline' }}
          onClick={fetchVoucherCouponList}
        >
          {buttonRefresh}
        </Text>
      </Flex>
      <Flex column alignItems="stretch">
        <Flex padding="0.625rem 0">
          <OnlyInput
            name={'code'}
            value={codeInputValue}
            onChange={(e) => setCodeInputValue(e.target.value)}
            placeholder={placeHolderCode}
            flex={3}
            background="white"
          />
          <Button
            onClick={() => registerVoucherCoupon(codeInputValue)}
            style={{ flex: 1, marginLeft: '0.938rem', height: '2.5rem' }}
            isLoading={isRegisterLoading}
          >
            <Text size={14}>{buttonEnroll}</Text>
          </Button>
        </Flex>
        <Text color="orangePink" size={14}>
          {voucherCouponError}
        </Text>
      </Flex>
      {!isCheckBoxOn && (
        <HorizonGreyLine
          style={{ marginTop: '0.625rem', marginBottom: '1.25rem' }}
        />
      )}
      {filteredVoucherCouponList.length > 0 ? (
        filteredVoucherCouponList.map((voucherCoupon) => {
          const {
            id,
            status,
            expirationDate,
            itemName,
            additionalInfo: { senderName },
            voucherType,
            options,
            policy,
            // code,
          } = voucherCoupon;
          
          // 쿠폰 상태 관리를 위한 변수
          const isVoucherSelected = selectedVoucherCouponList.map(id => voucherCouponList.find(el => el.id === id).voucherType).some(type => type !== 'COUPON');
          const isVoucher = voucherType !== 'COUPON';
          const isUnusableVoucher = isVoucher && couponOnly === true;
          const isUnusableCoupon = !isVoucher && isVoucherSelected;
          const isExpired = status === 'EXPIRED' || (status === 'DELEGATED' && calculateDday(expirationDate) < 0);
          const isUsed = status === 'USED';
          const isEnable = !(isUnusableVoucher || isUnusableCoupon || isExpired || isUsed);

          // 상품권 옵션 관련 변수
          const policyOptionIds = [policy, ...policy.childPolicies].filter(p => p.targetType === 'OPTION').map(p => p.targetId);
          const purchasedOptionIds = options.length > 0 ? options.map(o => o.option.dependency ? o.option.dependency.id : o.option.id) : [];
          const voucherOptionIds = [...new Set([...policyOptionIds, ...purchasedOptionIds])];
          
          const onClickCoupon = async () => {
            // 체크 박스가 비활성화 된 상태 (쿠폰 목록) - 상품권만 사용 가능
            if (!isCheckBoxOn) {
              if (isEnable && isVoucher) {
                const itemId = await getBabyfaceItemId();
                sessionStorage.itemId = itemId;
                sessionStorage.optionIds = JSON.stringify(voucherOptionIds);
                sessionStorage.preSelectedVoucherId = id;
                history.push('/order');
              }
              return;
            }

            // 선택된 옵션 관련 변수
            const optionIds = JSON.parse(sessionStorage.optionIds);
            const optionMatched = optionIds.every(el => voucherOptionIds.includes(el));
            
            // 상품권 사용 불가일 경우 안내
            if (isUnusableVoucher) {
              if (window.confirm('해당 상품권은 바로 구매에서만 사용 하실 수 있습니다. 바로 구매로 이동 하시겠습니까?')) history.replace('/order');
              return;
            } else if (isUnusableCoupon) {
              window.alert('해당 쿠폰은 상품권과 같이 사용할 수 없습니다.');
              return;
            } else if (isVoucher) {
              // 상품권 옵션이 선택한 옵션과 일치하지 않는 경우
              if (!optionMatched) {
                if (window.confirm('상품권 옵션이 일치하지 않습니다. 해당 상품권의 옵션으로 구매 하시겠습니까?')) {
                  sessionStorage.optionIds = JSON.stringify(voucherOptionIds);
                  sessionStorage.preSelectedVoucherId = id;
                  history.go(0);
                }
                return;
              }
            }

            const couponSelectionList = isVoucher ? (selectedVoucherCouponList.includes(id) ? [] : [id]) : getVoucherCouponSelectionList(id);
            if (!couponSelectionList) {
              window.alert(STATUS_MESSAGE.voucher[500]);
              return;
            }
            onSelectVoucherCoupon(couponSelectionList, setVoucherCouponSelectionList, setIsLoading);
          }

          return (
            <CouponListContainer
              key={id}
              active={selectedVoucherCouponList.includes(id)}
              onClick={onClickCoupon}
              isPointer={isEnable}
            >
              <Flex
                alignItems="flex-start"
                justifyContent="space-between"
                width="100%"
              >
                <Flex alignItems="flex-start">
                  {isCheckBoxOn && (
                    <CheckIcon checked={selectedVoucherCouponList.includes(id)} />
                  )}
                  <Flex column padding="0 0.625rem" alignItems="flex-start">
                    <Text
                      size={14}
                      weight="medium"
                      color={isEnable ? 'darkest' : 'brownGrey'}
                    >
                      {itemName}
                    </Text>
                    <Text
                      color="brownGrey"
                      size={12}
                      style={{ margin: '0.313rem 0' }}
                    >
                      {`${replaceDateFormat(expirationDate)} `}
                      {isEnable && (
                        <Text size={12} color="orangePink">
                          D-{calculateDday(expirationDate)}
                        </Text>
                      )}
                    </Text>
                    {voucherType !== 'PRE_ORDER' && (
                      <Text size={12} color={'brownGrey'}>
                        From. {senderName}
                      </Text>
                    )}
                  </Flex>
                </Flex>
                <Text
                  size={14}
                  color={
                    isEnable && !isCheckBoxOn
                      ? 'darkest'
                      : 'brownGrey'
                  }
                  style={{ minWidth: '3.25rem' }}
                  textAlign="end"
                >
                  {isEnable && !isCheckBoxOn && isVoucher && notiUse}
                  {isEnable && !isCheckBoxOn && !isVoucher && notiDelegated}
                  {(isUnusableVoucher || isUnusableCoupon) && notiUnusable}
                  {isUsed && notiUsed}
                  {isExpired && notiExpired}
                </Text>
              </Flex>
            </CouponListContainer>
          );
        })
      ) : (
        <Flex column padding="1.25rem 0">
          <Text size={14} weight="medium">
            {notiEmptyVoucher}
          </Text>
        </Flex>
      )}
    </>
  );
};

VoucherCoupon.propTypes = {
  status: PropTypes.oneOf(['delegated', 'usable']).isRequired,
};

VoucherCoupon.defaultProps = {
  couponOnly: false,
  onSelectVoucherCoupon: undefined,
};

export default React.memo(VoucherCoupon);

const VOUCHER = {
  notiVoucher: '사용 가능한 상품권/쿠폰',
  labelAccount: '장',
  buttonRefresh: '새로고침',
  notiRefresh: '발급받으신 쿠폰이 보이지 않으면 ‘새로고침’을 눌러주세요.',
  placeHolderCode: '코드를 입력하세요',
  buttonEnroll: '등록',
  notiEmptyVoucher: '사용 가능한 상품권/쿠폰이 없습니다.',
  notiDelegated: '미사용',
  notiUse: '사용하기',
  notiUsed: '사용',
  notiExpired: '만료',
  notiUnusable: '사용불가',
};

const CouponListContainer = styled(Flex)`
  justify-content: space-between;
  padding: 1rem;
  margin-top: 0.625rem;
  border-radius: 6px;
  border: solid
    ${(props) =>
      props.active
        ? `2px ${props.theme.color.orangePink}`
        : `1px ${props.theme.color.pinkishGrey}`};
  color: ${(props) =>
    props.theme.color[props.active ? 'orangePink' : 'pinkishGrey']};
  cursor: ${(props) => props.isPointer && 'pointer'};
`;
